import { useMemo } from 'react';
import { formatSnakeCaseToGeneral } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { IAuthorizedUserDtoStatusEnum } from '@/dtos';

import { AuthorizedUser } from '../../../authorizedUsers.types';

export const COLUMNS = [
  { key: 'firstName', title: 'First name', flex: '1' as const },
  { key: 'lastName', title: 'Last name', flex: '1' as const },
  { key: 'relationship', title: 'Relationship', flex: '1' as const },
  { key: 'dateOfBirth', title: 'Date of birth', flex: '1' as const },
  { key: 'dateAdded', title: 'Date added', flex: '1' as const },
  { key: 'status', title: 'Status', flex: '1' as const },
];

export const ACTION_COLUMN_FLEX = '0 0 40px' as const;

const FIELD_PLACEHOLDER = '-';

export interface FormattedAuthorizedUser {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  dateOfBirth: string;
  dateAdded: string;
  status: IAuthorizedUserDtoStatusEnum;
  isActive: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}

interface Props {
  authorizedUsers: AuthorizedUser[];
  onEdit: (user: AuthorizedUser) => void;
  onDeactivate: (user: AuthorizedUser) => void;
  onReactivate: (user: AuthorizedUser) => void;
}

const useAuthorizedUsersList = ({ authorizedUsers, onEdit, onDeactivate, onReactivate }: Props) => {
  const formattedUsers = useMemo<FormattedAuthorizedUser[]>(
    () =>
      authorizedUsers.map((user) => ({
        id: user.id,
        firstName: user.firstName || FIELD_PLACEHOLDER,
        lastName: user.lastName || FIELD_PLACEHOLDER,
        relationship: capitalize(formatSnakeCaseToGeneral(user.relationshipType ?? '')),
        dateOfBirth: user.birthOn ? dayjs(user.birthOn).format(DEFAULT_DATE_FORMAT) : FIELD_PLACEHOLDER,
        dateAdded: user.createdAt ? dayjs(user.createdAt).format(DEFAULT_DATE_FORMAT) : FIELD_PLACEHOLDER,
        status: user.status,
        isActive: user.status === IAuthorizedUserDtoStatusEnum.ACTIVE,
        onEdit: () => onEdit(user),
        onDeactivate: () => onDeactivate(user),
        onReactivate: () => onReactivate(user),
      })),
    [authorizedUsers, onEdit, onDeactivate, onReactivate]
  );

  return { formattedUsers };
};

export default useAuthorizedUsersList;
